import apiClient from './apiClient';
import { mapUserFromBackend } from './apiUtils';
import { AuthResult, LoginRequest, RegisterUserRequest, UpdateUserRequest, User } from '../types';

// Mock users for testing
const mockUsers: User[] = [
  {
    id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
    account: 'admin@secureguard.com',
    name: 'Admin User',
    role: 'Admin',
    companyId: '11111111-1111-1111-1111-111111111111',
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString()
  },
  {
    id: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb',
    account: 'manager@secureguard.com',
    name: 'Manager User',
    role: 'Manager',
    companyId: '11111111-1111-1111-1111-111111111111',
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString()
  },
  {
    id: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
    account: 'guard@secureguard.com',
    name: 'Guard User',
    role: 'Guard',
    companyId: '11111111-1111-1111-1111-111111111111',
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString()
  },
  {
    id: 'dddddddd-dddd-dddd-dddd-dddddddddddd',
    account: 'admin@safetyfirst.com',
    name: 'Admin Safety',
    role: 'Admin',
    companyId: '22222222-2222-2222-2222-222222222222',
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString()
  }
];

// Mock credentials for testing
const mockCredentials: { [key: string]: string } = {
  'admin@secureguard.com': 'Admin123!',
  'manager@secureguard.com': 'Manager123!',
  'guard@secureguard.com': 'Guard123!',
  'admin@safetyfirst.com': 'Admin123!'
};

/**
 * Service for authentication and user management
 */
export class AuthService {
  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResult> {
    console.log('Attempting login with:', credentials);
    
    try {
      // Make a real API call
      const response = await apiClient.post<AuthResult>('/auth/login', credentials);
      
      if (response.success && response.data) {
        // Store token in apiClient for future requests
        apiClient.setAuthToken(response.data.token);
        
        // Map the user from backend format to frontend format
        if (response.data.user) {
          const mappedUser = mapUserFromBackend(response.data.user);
          if (mappedUser) {
            response.data.user = mappedUser;
          }
        }
        
        console.log('Login successful:', response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Comment out mock data fallback to use the real API
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('Using mock authentication in development mode');
      //   
      //   // Check if the account exists in our mock users
      //   const user = mockUsers.find(u => u.account === credentials.account);
      //   
      //   if (!user) {
      //     console.error('User not found');
      //     throw new Error('Invalid email or password');
      //   }
      //   
      //   // Check if the password matches
      //   if (mockCredentials[credentials.account] !== credentials.password) {
      //     console.error('Password mismatch');
      //     throw new Error('Invalid email or password');
      //   }
      //   
      //   // Create a mock token
      //   const token = `mock_token_${Date.now()}`;
      //   const expiration = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // 24 hours from now
      //   
      //   // Create the auth result
      //   const authResult: AuthResult = {
      //     success: true,
      //     token,
      //     expiration,
      //     user
      //   };
      //   
      //   // Store token in apiClient for future requests
      //   apiClient.setAuthToken(token);
      //   
      //   // Update last login time
      //   user.lastLoginAt = new Date().toISOString();
      //   
      //   console.log('Mock login successful:', authResult);
      //   return authResult;
      // }
      
      // In production, rethrow the error
      throw error;
    }
  }

  async respondToChallenge(account: string, newPassword: string, session: string): Promise<AuthResult> {
    const response = await apiClient.post<AuthResult>('/auth/respond-to-challenge', {
      Account: account,
      NewPassword: newPassword,
      Session: session,
    });

    if (response.success && response.data) {
      apiClient.setAuthToken(response.data.token);
      if (response.data.user) {
        const mappedUser = mapUserFromBackend(response.data.user);
        if (mappedUser) {
          response.data.user = mappedUser;
        }
      }
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to set new password');
    }
  }

  /**
   * Register a new user
   */
  async register(userData: RegisterUserRequest): Promise<AuthResult> {
    console.log('Registering new user:', userData);
    
    try {
      // Convert camelCase properties to PascalCase for backend
      const backendData = {
        FirstName: userData.firstName,
        LastName: userData.lastName,
        Account: userData.account,
        Password: userData.password,
        Role: userData.role,
        ParentUserId: userData.parentUserId,
        CompanyId: userData.companyId,
        CertificateNumber: userData.certificateNumber || null,
        CertificateExpiration: userData.certificateExpiration ? userData.certificateExpiration + 'T00:00:00Z' : null,
      };

      // Make a real API call with properly cased data
      const response = await apiClient.post<AuthResult>('/auth/register', backendData);
      
      if (response.success && response.data) {
        // Map the user from backend format to frontend format
        if (response.data.user) {
          const mappedUser = mapUserFromBackend(response.data.user);
          if (mappedUser) {
            response.data.user = mappedUser;
          }
        }
        
        console.log('Registration successful:', response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Only use mock data in development mode
      if (process.env.NODE_ENV === 'development' && typeof localStorage !== 'undefined') {
        console.log('Using mock registration in development mode');

        // Check if account already exists
        if (mockUsers.some(u => u.account === userData.account)) {
          throw new Error('Account already exists');
        }

        // Get the current user's company ID from localStorage
        const currentUserJson = localStorage.getItem('user');
        if (!currentUserJson) {
          console.error('No current user found');
          throw new Error('Authentication required');
        }
        
        const currentUser = JSON.parse(currentUserJson) as User;
        
        // Create a new user with the same company ID as the current user
        const newUser: User = {
          id: `user_${Date.now()}`,
          account: userData.account,
          name: userData.name,
          role: userData.role,
          companyId: currentUser.companyId,
          createdAt: new Date().toISOString()
        };
        
        // Add to mock users
        mockUsers.push(newUser);
        
        // Add credentials
        mockCredentials[userData.account] = userData.password;
        
        // Create a mock token
        const token = `mock_token_${Date.now()}`;
        const expiration = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // 24 hours from now
        
        // Create the auth result
        const authResult: AuthResult = {
          success: true,
          token,
          expiration,
          user: newUser
        };
        
        console.log('Mock registration successful:', authResult);
        return authResult;
      }
      
      // In production, rethrow the error
      throw error;
    }
  }

  /**
   * Get all users (for admin/manager)
   */
  async getUsers(): Promise<User[]> {
    console.log('Getting all users');
    
    try {
      // Make a real API call
      const response = await apiClient.get<User[]>('/auth/users');
      
      if (response.success && response.data) {
        console.log('Successfully retrieved users:', response.data);
        // Map backend users to frontend format and filter out null values
        const mappedUsers = response.data
          .map(user => mapUserFromBackend(user))
          .filter(user => user !== null) as User[];
        return mappedUsers;
      } else {
        throw new Error(response.message || 'Failed to retrieve users');
      }
    } catch (error) {
      console.error('Error retrieving users:', error);
      
      // Only use mock data in development mode
      if (process.env.NODE_ENV === 'development' && typeof localStorage !== 'undefined') {
        console.log('Using mock users data in development mode');
        
        // Get the current user from localStorage
        const currentUserJson = localStorage.getItem('user');
        if (!currentUserJson) {
          console.error('No current user found');
          return [];
        }
        
        const currentUser = JSON.parse(currentUserJson) as User;
        
        // Filter users by company ID
        const filteredUsers = mockUsers.filter(user => user.companyId === currentUser.companyId);
        
        console.log(`Filtered users by company ID ${currentUser.companyId}:`, filteredUsers);
        return [...filteredUsers];
      }
      
      // In production, rethrow the error
      throw error;
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<User> {
    console.log('Getting user by ID:', id);
    
    try {
      // Make a real API call
      const response = await apiClient.get<User>(`/auth/users/${id}`);
      
      if (response.success && response.data) {
        console.log('Successfully retrieved user:', response.data);
        // Map backend user to frontend format
        const mappedUser = mapUserFromBackend(response.data);
        if (mappedUser) {
          return mappedUser;
        } else {
          throw new Error('Failed to map user data');
        }
      } else {
        throw new Error(response.message || 'Failed to retrieve user');
      }
    } catch (error) {
      console.error('Error retrieving user:', error);
      
      // Only use mock data in development mode
      if (process.env.NODE_ENV === 'development' && typeof localStorage !== 'undefined') {
        console.log('Using mock user data in development mode');
        
        // Get the current user from localStorage
        const currentUserJson = localStorage.getItem('user');
        if (!currentUserJson) {
          console.error('No current user found');
          throw new Error('Authentication required');
        }
        
        const currentUser = JSON.parse(currentUserJson) as User;
        
        // Find the user by ID
        const user = mockUsers.find(u => u.id === id);
        
        if (!user) {
          throw new Error('User not found');
        }
        
        // Check if the user belongs to the same company
        if (user.companyId !== currentUser.companyId) {
          throw new Error('Access denied: User belongs to a different company');
        }
        
        return { ...user };
      }
      
      // In production, rethrow the error
      throw error;
    }
  }

  /**
   * Update user
   */
  async updateUser(id: string, userData: UpdateUserRequest): Promise<boolean> {
    console.log('Updating user:', id, userData);
    
    try {
      // Try multiple approaches to find the right format
      
      // 1. First, try with the updateUserDto wrapper
      let roleValue: number | undefined;
      if (userData.role) {
        if (typeof userData.role === 'string') {
          const roleLower = userData.role.toLowerCase();
          roleValue = roleLower === 'admin' ? 0 : roleLower === 'manager' ? 1 : 3; // Guard is 3
        } else {
          roleValue = userData.role;
        }
      }
      
      // Create the DTO object with correct casing for backend
      const updateUserDto = {
        FirstName: userData.firstName || '',
        LastName: userData.lastName || '',
        Account: userData.account || '',
        Password: userData.password || '', // Empty password means no change
        Role: roleValue,
        ParentUserId: userData.parentUserId,
        CertificateNumber: userData.certificateNumber,
        CertificateExpiration: userData.certificateExpiration ? userData.certificateExpiration + 'T00:00:00Z' : null,
      };

      console.log('Direct DTO approach:', JSON.stringify(updateUserDto, null, 2));
      
      // Make a real API call with the direct DTO
      const response = await apiClient.put<void>(`/auth/users/${id}`, updateUserDto);
      
      if (response.success) {
        console.log('Successfully updated user');
        return true;
      } else {
        throw new Error(response.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      
      // Only use mock data in development mode
      if (process.env.NODE_ENV === 'development' && typeof localStorage !== 'undefined') {
        console.log('Using mock user update in development mode');
        
        // Get the current user from localStorage
        const currentUserJson = localStorage.getItem('user');
        if (!currentUserJson) {
          console.error('No current user found');
          throw new Error('Authentication required');
        }
        
        const currentUser = JSON.parse(currentUserJson) as User;
        
        // Find the user to update
        const userIndex = mockUsers.findIndex(u => u.id === id);
        
        if (userIndex === -1) {
          throw new Error('User not found');
        }
        
        // Check if the user belongs to the same company
        if (mockUsers[userIndex].companyId !== currentUser.companyId) {
          throw new Error('Access denied: User belongs to a different company');
        }
        
        // Update user data
        mockUsers[userIndex] = {
          ...mockUsers[userIndex],
          ...userData
        };
        
        // Update password if provided
        if (userData.password) {
          mockCredentials[mockUsers[userIndex].account] = userData.password;
        }
        
        return true;
      }
      
      // In production, rethrow the error
      throw error;
    }
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<boolean> {
    console.log('Deleting user:', id);
    
    try {
      // Make a real API call
      const response = await apiClient.delete<void>(`/auth/users/${id}`);
      
      if (response.success) {
        console.log('Successfully deleted user');
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      
      // Only use mock data in development mode
      if (process.env.NODE_ENV === 'development' && typeof localStorage !== 'undefined') {
        console.log('Using mock user deletion in development mode');
        
        // Get the current user from localStorage
        const currentUserJson = localStorage.getItem('user');
        if (!currentUserJson) {
          console.error('No current user found');
          throw new Error('Authentication required');
        }
        
        const currentUser = JSON.parse(currentUserJson) as User;
        
        // Find the user to delete
        const userIndex = mockUsers.findIndex(u => u.id === id);
        
        if (userIndex === -1) {
          throw new Error('User not found');
        }
        
        // Check if the user belongs to the same company
        if (mockUsers[userIndex].companyId !== currentUser.companyId) {
          throw new Error('Access denied: User belongs to a different company');
        }
        
        // Remove from mock users
        const account = mockUsers[userIndex].account;
        mockUsers.splice(userIndex, 1);
        
        // Remove credentials
        delete mockCredentials[account];
        
        return true;
      }
      
      // Extract the error message from the API response if available
      if (error instanceof Error) {
        const axiosError = error as any;
        if (axiosError.response && axiosError.response.data && axiosError.response.data.message) {
          throw new Error(axiosError.response.data.message);
        }
      }
      
      // In production, rethrow the error
      throw error;
    }
  }

  /**
   * Refresh token
   * With Cognito, only the refresh token is needed (not the expired access token).
   * For web clients the refresh token may be sent via HttpOnly cookie automatically.
   * For mobile clients the refresh token is passed explicitly.
   */
  async refreshToken(refreshToken?: string): Promise<AuthResult> {
    console.log('Refreshing token');

    // Validate that we have the required tokens before sending the request.
    // The backend needs the (possibly expired) access token to extract the Cognito username.
    const accessToken = apiClient.getAuthToken();
    const resolvedRefreshToken = refreshToken || apiClient.getRefreshToken();

    if (!accessToken) {
      console.warn('No access token available for refresh — user must log in again');
      throw new Error('Authentication required — no access token available');
    }

    try {
      // Web: refresh token is sent automatically via HttpOnly cookie (resolvedRefreshToken may be empty)
      // Mobile: refresh token is passed explicitly from AsyncStorage / in-memory store
      const response = await apiClient.post<AuthResult>('/auth/refresh-token', {
        token: accessToken,
        refreshToken: resolvedRefreshToken || undefined,
      });
      
      if (response.success && response.data) {
        // Store token in apiClient for future requests
        apiClient.setAuthToken(response.data.token);

        // Map the user from backend format to frontend format (same as login())
        if (response.data.user) {
          const mappedUser = mapUserFromBackend(response.data.user);
          if (mappedUser) {
            response.data.user = mappedUser;
          }
        }

        console.log('Token refreshed successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to refresh token');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }

  /**
   * Revoke refresh token
   */
  async revokeToken(): Promise<boolean> {
    console.log('Revoking token');

    try {
      // Send refresh token in body for mobile clients (web uses HttpOnly cookie)
      const refreshToken = apiClient.getRefreshToken();
      const response = await apiClient.post<void>('/auth/revoke-token', {
        refreshToken: refreshToken || undefined,
      });
      
      if (response.success) {
        console.log('Token revoked successfully');
        return true;
      } else {
        throw new Error(response.message || 'Failed to revoke token');
      }
    } catch (error) {
      console.error('Error revoking token:', error);
      throw error;
    }
  }

  /**
   * Logout - clear token and revoke refresh token
   */
  async logout(): Promise<void> {
    console.log('Logging out');
    
    try {
      // Revoke the refresh token (sent via HttpOnly cookie)
      await this.revokeToken();
    } catch (error) {
      console.error('Error revoking token during logout:', error);
    }
    
    // Clear token from apiClient
    apiClient.clearAuthToken();
  }

  /**
   * Confirm password reset with verification code
   */
  async confirmPasswordReset(account: string, code: string, newPassword: string): Promise<boolean> {
    const response = await apiClient.post<void>('/auth/confirm-reset-password', { account, code, newPassword });
    return response.success;
  }

  /**
   * Admin reset password for a specific user
   */
  async adminResetPassword(userId: string, newPassword: string): Promise<boolean> {
    const response = await apiClient.post<void>(`/auth/users/${userId}/reset-password`, { newPassword });
    return response.success;
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(account: string): Promise<boolean> {
    console.log('Requesting password reset for:', account);
    
    try {
      // Make a real API call
      const response = await apiClient.post<void>('/auth/reset-password', { account });
      
      if (response.success) {
        console.log('Successfully requested password reset');
        return true;
      } else {
        throw new Error(response.message || 'Failed to request password reset');
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      
      // Only use mock data in development mode
      if (process.env.NODE_ENV === 'development' && typeof localStorage !== 'undefined') {
        console.log('Using mock password reset in development mode');
        
        // Get the current user from localStorage
        const currentUserJson = localStorage.getItem('user');
        if (!currentUserJson) {
          console.error('No current user found');
          throw new Error('Authentication required');
        }
        
        const currentUser = JSON.parse(currentUserJson) as User;
        
        // Find the user by account
        const user = mockUsers.find(u => u.account === account);
        
        // Check if account exists
        if (!user) {
          throw new Error('Account not found');
        }
        
        // Check if the user belongs to the same company
        if (user.companyId !== currentUser.companyId) {
          throw new Error('Access denied: User belongs to a different company');
        }
        
        // In a real app, this would send an email to the account
        console.log(`Password reset link would be sent to ${account}`);
        
        return true;
      }
      
      // In production, rethrow the error
      throw error;
    }
  }
}

// Create a default instance
export const authService = new AuthService();

export default authService;
